import { defineComponent as _defineComponent } from 'vue'
import { User } from '@/models/user';
import { customersService } from '@/services/tenants.service';
import { isEmpty, isNil, uniqueId } from '@/utils/misc';
import { Logger } from '@/utils/logger';
import { useI18n } from '@/composables/i18n';
import { currentUser } from '@/utils/permissionUtils';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { EmailEntry } from '@/models/EmailEntry';
import { ValidationRules } from '@/utils/validation';
import useVuelidate from '@vuelidate/core';


export default /*#__PURE__*/_defineComponent({
  __name: 'EmailsTagInput',
  props: {
    value: { default: () => [] },
    disabled: { type: Boolean, default: false },
    customerId: null,
    showLabel: { type: Boolean, default: true },
    selectAllInit: { type: Boolean, default: false },
    filterUsers: { type: Function, default: () => () => true },
    loading: { type: Boolean, default: false },
    validationRules: { default: () => ({}) }
  },
  emits: ["input"],
  setup(__props: any, { expose, emit }: { emit: ((e: 'input', value: EmailEntry[]) => void), expose: any, slots: any, attrs: any }) {

const props = __props as { value: string[], disabled: boolean, customerId?: number, showLabel: boolean, selectAllInit: boolean, filterUsers: (user: User, index?: number, array?: User[]) => boolean, loading: boolean, validationRules: ValidationRules };





const { t } = useI18n();
const componentId = uniqueId();

const users = ref<User[]>([]);
const availableEmails = ref<string[]>([]);
const nonSelectedEmails = ref<string[]>([]);

const areFilteredCustomersLoading = ref<boolean>(false);

const state = reactive({
  emails: props.value,
});
const validations = {
  emails: props.validationRules,
};
const v$ = useVuelidate(validations, state);

onMounted(async () => {
  initializeTag();
  await loadData();
  if (props.selectAllInit) {
    selectAll();
  }
});

async function loadData() {
  areFilteredCustomersLoading.value = true;
  users.value = await getFilteredCustomerUsers().finally(
    () => (areFilteredCustomersLoading.value = false),
  );
  availableEmails.value = users.value.map((user) => user.email);
  nonSelectedEmails.value = availableEmails.value.filter(
    (option) => !state.emails.includes(option),
  );
}

async function getFilteredCustomerUsers(): Promise<User[]> {
  const customerId = currentUser().customerId ?? props.customerId;
  if (isNil(customerId)) {
    Logger.error('Missing customer id from which to get its users');
    throw new Error('Missing customer id from which to get its users');
  }
  return (await customersService.getUsers(customerId)).filter(
    props.filterUsers,
  );
}

function initializeTag() {
  state.emails = valueOrDefault.value;
}

function selectAll() {
  state.emails = [...availableEmails.value];
  onSelectionChanged();
}

function clearAll() {
  state.emails = [];
  onSelectionChanged();
}

function updateNonSelectedEmails(text: string) {
  nonSelectedEmails.value = availableEmails.value.filter((option) => {
    return (
      option.toLowerCase().includes(text.toLowerCase()) &&
      !state.emails.includes(option)
    );
  });
}

function onSelectionChanged() {
  emit(
    'input',
    state.emails.map(
      (email) =>
        ({
          userId: users.value.find((user) => user.email === email)!.id,
          email,
        } as EmailEntry),
    ),
  );
}

const valueOrDefault = computed(() => props.value ?? []);

watch(valueOrDefault, () => {
  initializeTag();
});

const label = computed((): string | undefined => {
  if (!props.showLabel) {
    return undefined;
  }

  return t('inputs.alerts_email_select.label').toString();
});

const areTagsEmpty = computed(() => isEmpty(state.emails));

const isLoading = computed(
  (): boolean => props.loading || areFilteredCustomersLoading.value,
);

const required = computed(() => 'required' in props.validationRules);

const validationMessage = computed(() => v$.value.emails.$errors[0]?.$message);

expose({ initializeTag });

return { __sfc: true,props, emit, t, componentId, users, availableEmails, nonSelectedEmails, areFilteredCustomersLoading, state, validations, v$, loadData, getFilteredCustomerUsers, initializeTag, selectAll, clearAll, updateNonSelectedEmails, onSelectionChanged, valueOrDefault, label, areTagsEmpty, isLoading, required, validationMessage }
}

})